import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Check, X } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  getGetDoclistQueryKey,
  useRenameDoclistCategory,
} from "~/api/chat/chat";
import { getListCustomReportsByCaseQueryKey } from "~/api/custom-reports/custom-reports";
import { DoclistTreeItem } from "~/api/model";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";
import { useDoclistUpdatedAtStore } from "../../stores/doclistUpdatedAtStore";

interface DocListCategoryInputProps {
  category?: DoclistTreeItem;
  organizationId: string;
  projectId: string;
  onDoclistOutdated: () => void;
  onCancel: () => void;
  isNew?: boolean;
  color?: string;
  oldName?: string;
  onSubmit?: (name: string) => void;
  setItems?: React.Dispatch<React.SetStateAction<DoclistTreeItem[]>>;
}

export const DocListCategoryInput: FC<DocListCategoryInputProps> = ({
  category,
  organizationId,
  projectId,
  onDoclistOutdated,
  onCancel,
  isNew = false,
  color,
  oldName,
  onSubmit,
  setItems,
}) => {
  const [categoryName, setCategoryName] = useState(
    category?.category.name || oldName || ""
  );
  const [inputTouched, setInputTouched] = useState(false);
  const formCategoryRef = useRef<HTMLFormElement>(null);
  const queryClient = useQueryClient();
  const { mutateAsync: renameCategory } = useRenameDoclistCategory();
  const { setUpdatedAt } = useDoclistUpdatedAtStore();
  const updatedAt = useDoclistUpdatedAtStore((state) =>
    projectId ? state.updatedAtByProject[projectId] : undefined
  );
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    trackEvent({
      eventName: isNew
        ? "app/doc_list/add_category.submit"
        : "app/doc_list/rename_category.submit",
    });

    if (!categoryName.trim().length) return;
    if (!isNew && categoryName.trim() === category?.category.name.trim())
      return;

    if (isNew) {
      onSubmit?.(categoryName.trim());
      return;
    }

    if (!updatedAt) return;
    setItems?.((prevItems: DoclistTreeItem[]) =>
      prevItems.map((item: DoclistTreeItem) =>
        item.category.id === category!.category.id
          ? {
              ...item,
              category: { ...item.category, name: categoryName.trim() },
            }
          : item
      )
    );
    try {
      const response = await renameCategory({
        organizationId,
        projectId,
        categoryId: category!.category.id,
        data: {
          title: categoryName.trim(),
          updatedAt: updatedAt,
        },
      });
      setUpdatedAt(projectId, response.updatedAt);
      queryClient.invalidateQueries({
        queryKey: getGetDoclistQueryKey(organizationId, projectId),
      });
      queryClient.invalidateQueries({
        queryKey: getListCustomReportsByCaseQueryKey(organizationId, projectId),
      });
      setInputTouched(false);
      onCancel();
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 409) {
          onDoclistOutdated();
        }
      }
      logger.error(error);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e as any);
    } else if (e.key === "Escape") {
      setInputTouched(false);
      onCancel();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        formCategoryRef.current &&
        !formCategoryRef.current.contains(event.target as Node)
      ) {
        setInputTouched(false);
        onCancel();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <form onSubmit={handleSubmit} ref={formCategoryRef}>
      <div
        className="h-[36px] flex items-center gap-2.5 text-white border-black border border-opacity-30 pl-[9px] pr-[11px] py-[7px] rounded-md"
        style={{
          background: `linear-gradient(90deg, rgba(0, 0, 0, 0.00) 5.24%, rgba(0, 0, 0, 0.25) 93.21%), ${isNew ? color : category?.category.color}`,
          opacity: isNew ? 0.5 : 1,
          marginBottom: isNew ? "2px" : "0px",
        }}
      >
        <Input
          autoFocus
          value={categoryName}
          onChange={(e) => {
            setInputTouched(true);
            setCategoryName(e.target.value);
          }}
          onKeyDown={handleInputKeyDown}
          className="border-0 p-0 h-auto uppercase"
        />
        <div className="flex items-start gap-1 -mr-1">
          <Button
            type="submit"
            variant="outline"
            size="icon"
            className="w-6 h-6 rounded-sm"
          >
            <Check className="w-3 h-3 text-blue-600" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="w-6 h-6 rounded-sm"
            onClick={(e) => {
              e.preventDefault();
              onCancel();
            }}
          >
            <X className="w-3 h-3 text-red-600" />
          </Button>
        </div>
      </div>
      {!categoryName.trim().length && inputTouched && (
        <span className="text-sm text-destructive">
          <FormattedMessage
            id="docList.addCategory.empty"
            defaultMessage="The name can't be empty"
          />
        </span>
      )}
    </form>
  );
};
